import type { WindowDescriptor } from '@krp/client-runtime'

import { MAINTENANCE_MANAGE, PHOTO_SETTINGS, TESTING_SEARCH } from '../features/access/model/grants'

import { accessRoleChanged, decodeAccessRole } from '../features/access/model/accessSlice'
import { catalogIntentRestored, decodeCommonCatalogIntent } from '../features/catalog/model/commonCatalogSlice'
import type { PlatformUiState } from '../state'

/**
 * Окна пакета `platform-ui` — объявлены один раз (R11). Содержимое окна —
 * binding приложения; здесь адрес, подпись, меню и то, что помнит вкладка.
 */
export const PLATFORM_WINDOWS = {
  resources: 'platform.resources',
  resource: 'platform.resource',
  // Вкладка «Источники» (решение владельца 24.10.2026): «Добавить источник» — окно с плитками видов, дальше форма выбранного.
  sourceAdd: 'platform.source-add',
  resourceAdd: 'platform.resource-add',
  resourceEdit: 'platform.resource-edit',
  access: 'platform.access',
  diagnostics: 'platform.diagnostics',
  catalogLinks: 'platform.catalog-links',
  // RP-FB50: окно «Общий товар» — просмотр и правка общего товара (создание, поля, штрихкоды, архив, источники).
  commonProduct: 'platform.common-product',
  commonProductNew: 'platform.common-product-new',
  // RP-FB56: сканирование как пополнение базы — выбор товара общего списка и подтверждение «установить это фото?».
  productPick: 'platform.product-pick',
  photoAttach: 'platform.photo-attach',
  search: 'platform.search',
  // RP-FB4: живой сканер поиска по фото — во весь экран поверх поиска.
  scanner: 'platform.search-scanner',
  about: 'platform.about',
  // RP-MNT: пульт обновления касс — открытые РМК и режим обновления.
  maintenance: 'platform.maintenance',
  // RP-CACHE-0: настройки раздела «Фото» — сроки и потолок кэша фото.
  photoSettings: 'platform.photo-settings',
  // RP-FB29: вкладка «Тестирование» — виды проверок; первый вид — поиск по фото.
  testing: 'platform.testing',
  testingProducts: 'platform.testing-products',
  testingProduct: 'platform.testing-product',
  testingCheck: 'platform.testing-check',
  testingRuns: 'platform.testing-runs',
  testingRun: 'platform.testing-run',
  // RP-FB34: «Тестирование» → «Поиск по тексту»: запросы, проверка запроса и замеры.
  testingTextQueries: 'platform.testing-text-queries',
  testingTextCheck: 'platform.testing-text-check',
  testingTextQueryCheck: 'platform.testing-text-query-check',
  testingTextRuns: 'platform.testing-text-runs',
  testingTextRun: 'platform.testing-text-run',
  // Тестовые товары общие с поиском по фото, но из поиска по тексту — свой экран без фото.
  testingTextProducts: 'platform.testing-text-products',
  testingTextProduct: 'platform.testing-text-product',
  // RP-FB67: «Тестирование» → «Похожие варианты»: двойники из каталога, кадры вариантов, замер и выгрузка для калибровки.
  testingTwin: 'platform.testing-twin',
  // RP-KA4: оборудование ЭТОГО компьютера — программа-драйвер, драйвер кассы, касса.
  hardware: 'platform.hardware',
  // UI-SECTIONS: входы разделов меню. «Номенклатура» показывает общий каталог сама,
  // остальные ведут во вкладку ресурса, который умеет раздел.
  nomenclature: 'platform.nomenclature',
  // Вкладка «Настройка» раздела «Номенклатура»: ставка НДС товаров по источникам, массовая установка.
  nomenclatureSettings: 'platform.nomenclature-settings',
  // UI-RMK: раздел «РМК» вместо «Продажи» — один пункт меню, вкладки — кассовые источники.
  rmk: 'platform.section-rmk',
  receipts: 'platform.section-receipts',
  // Отдельный пункт меню рядом с «Чеки»: выручка и итоги по источникам; сами чеки в нём не ищутся.
  salesStatistics: 'platform.section-sales-statistics',
  // «Смены» (решение владельца 26.10.2026): смены источника, как они хранятся в нём; вход — выбор источника плитками.
  shifts: 'platform.section-shifts',
  supplies: 'platform.section-supplies',
  prices: 'platform.section-prices',
  inventory: 'platform.section-inventory',
  priceTags: 'platform.section-price-tags',
  import: 'platform.section-import',
  structure: 'platform.section-structure',
} as const

/**
 * Разделы меню (UI-SECTIONS, решение владельца 09.10.2026): бизнес-области, а не
 * источники. Окна пакетов видов приходят в раздел вкладками по ИМЕНИ раздела
 * (`section.id`): пакеты друг друга не импортируют, имя сверяет реестр окон при
 * сборке приложения. Порядок — как работают за день.
 */
export const PLATFORM_SECTIONS = {
  rmk: 'rmk',
  receipts: 'receipts',
  salesStatistics: 'sales-statistics',
  shifts: 'shifts',
  nomenclature: 'nomenclature',
  supplies: 'supplies',
  prices: 'prices',
  inventory: 'inventory',
  priceTags: 'price-tags',
  import: 'import',
  structure: 'structure',
} as const

/** Вход раздела без собственного содержимого: открывает вкладку ресурса или объясняет, почему их нет. */
function sectionHome(
  kind: string,
  section: string,
  path: string | readonly string[],
  label: string,
  order: number,
  icon: string,
  pickResource = false,
): WindowDescriptor<PlatformUiState> {
  const paths = typeof path === 'string' ? [path] : path
  return {
    kind,
    scope: 'global',
    paths,
    label,
    menu: { group: 'work', order, icon },
    section: { id: section, home: pickResource ? { pickResource: true } : {} },
  }
}

export const platformWindows: readonly WindowDescriptor<PlatformUiState>[] = [
  {
    kind: PLATFORM_WINDOWS.resources,
    scope: 'global',
    paths: ['/resources'],
    label: 'Источники',
    menu: { group: 'settings', order: 10, icon: 'resources' },
  },
  {
    // Плитки видов; выбранная плитка заменяет это окно формой «Новый источник» этого вида, «Назад к плиткам» — обратно.
    kind: PLATFORM_WINDOWS.sourceAdd,
    scope: 'global',
    paths: ['/source-add'],
    label: 'Добавить источник',
    size: 'lg',
  },
  {
    kind: PLATFORM_WINDOWS.resource,
    scope: 'resource',
    paths: ['/resources/:rid'],
    label: 'Ресурс',
    size: 'md',
  },
  {
    // Вид — из адреса: форму настроек даёт пакет этого вида. Черновик (и секрет) вкладка НЕ помнит.
    kind: PLATFORM_WINDOWS.resourceAdd,
    scope: 'global',
    paths: ['/resource-add/:kind'],
    label: 'Новый источник',
    size: 'lg',
  },
  {
    kind: PLATFORM_WINDOWS.resourceEdit,
    scope: 'resource',
    paths: ['/resources/:rid/edit'],
    label: 'Изменить ресурс',
    size: 'lg',
  },
  {
    kind: PLATFORM_WINDOWS.access,
    scope: 'global',
    paths: ['/access'],
    label: 'Права к ресурсам',
    menu: { group: 'settings', order: 25, icon: 'access' },
    capture: (state) => ({ role: state.access.role }),
    restore: (snapshot) => {
      const role = decodeAccessRole(snapshot.role)
      return role === undefined ? [] : [accessRoleChanged(role)]
    },
  },
  {
    kind: PLATFORM_WINDOWS.diagnostics,
    scope: 'global',
    paths: ['/diagnostics'],
    label: 'Диагностика',
    menu: { group: 'settings', order: 30, icon: 'diagnostics' },
  },
  {
    // Версии служб через шлюз (AU1b): что работает сейчас, а не что было при открытии.
    kind: PLATFORM_WINDOWS.about,
    scope: 'global',
    paths: ['/about'],
    label: 'О приложении',
    menu: { group: 'settings', order: 40, icon: 'about' },
  },
  {
    // RP-MNT: пульт обновления — какие РМК открыты и что на них (корзина, оплата, заблокирована); включить/выключить режим. Только по праву.
    kind: PLATFORM_WINDOWS.maintenance,
    scope: 'global',
    paths: ['/maintenance'],
    label: 'Обновление касс',
    menu: { group: 'settings', order: 38, icon: 'sale', requires: MAINTENANCE_MANAGE },
  },
  {
    // RP-CACHE-0: сроки временных загрузок и кадров поиска, потолок кэша фото. Пункт меню только по праву; прямой адрес без права — «нет доступа».
    kind: PLATFORM_WINDOWS.photoSettings,
    scope: 'global',
    paths: ['/photo-settings'],
    label: 'Фото',
    menu: { group: 'settings', order: 37, icon: 'photo', requires: PHOTO_SETTINGS },
  },
  {
    // RP-FB29: пункт меню только у того, кому выдано право замеров; прямой адрес без права — «нет доступа».
    kind: PLATFORM_WINDOWS.testing,
    scope: 'global',
    paths: ['/testing'],
    label: 'Тестирование',
    menu: { group: 'settings', order: 35, icon: 'testing', requires: TESTING_SEARCH },
  },
  // Экраны замеров — страницы внутри «Тестирования», а не окна поверх: «← Назад» ведёт на шаг вверх (решение владельца 17.10.2026).
  {
    kind: PLATFORM_WINDOWS.testingProducts,
    scope: 'global',
    paths: ['/testing/photo-search'],
    label: 'Поиск по фото · Товары',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingProduct,
    scope: 'global',
    paths: ['/testing/photo-search/products/:productId'],
    label: 'Тестовый товар',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingCheck,
    scope: 'global',
    paths: ['/testing/photo-search/frames/:photoId'],
    label: 'Проверка кадра',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingRuns,
    scope: 'global',
    paths: ['/testing/photo-search/runs'],
    label: 'Поиск по фото · Замеры',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingRun,
    scope: 'global',
    paths: ['/testing/photo-search/runs/:runId'],
    label: 'Прогон замера',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextQueries,
    scope: 'global',
    paths: ['/testing/text-search'],
    label: 'Поиск по тексту · Запросы',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextCheck,
    scope: 'global',
    paths: ['/testing/text-search/check'],
    label: 'Поиск по тексту · Проверка',
    presentation: 'page',
  },
  {
    // Проверка сохранённого запроса — тот же экран, но запрос и товар уже заданы и проверка идёт сразу.
    kind: PLATFORM_WINDOWS.testingTextQueryCheck,
    scope: 'global',
    paths: ['/testing/text-search/queries/:queryId/check'],
    label: 'Поиск по тексту · Проверка запроса',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextRuns,
    scope: 'global',
    paths: ['/testing/text-search/runs'],
    label: 'Поиск по тексту · Замеры',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextRun,
    scope: 'global',
    paths: ['/testing/text-search/runs/:runId'],
    label: 'Замер поиска по тексту',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextProducts,
    scope: 'global',
    paths: ['/testing/text-search/products'],
    label: 'Поиск по тексту · Товары',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTextProduct,
    scope: 'global',
    paths: ['/testing/text-search/products/:productId'],
    label: 'Тестовый товар',
    presentation: 'page',
  },
  {
    kind: PLATFORM_WINDOWS.testingTwin,
    scope: 'global',
    paths: ['/testing/twin'],
    label: 'Похожие варианты',
    presentation: 'page',
  },
  {
    // Оборудование принадлежит компьютеру, а не рабочему месту: окно ничего не берёт с сервера.
    kind: PLATFORM_WINDOWS.hardware,
    scope: 'global',
    paths: ['/hardware', '/hardware/:tab'],
    label: 'Оборудование',
    menu: { group: 'settings', order: 35, icon: 'hardware' },
  },
  {
    // FIX-A2: связи карточек ресурсов с общим товаром — вход синхронизации.
    kind: PLATFORM_WINDOWS.catalogLinks,
    scope: 'global',
    paths: ['/catalog/links'],
    label: 'Связи товаров',
    // RP-EN2: подэкран синхронизации номенклатуры; из меню убран, открывается из окна ресурса и из правила.
    size: 'lg',
  },
  {
    // RP-FB50: общий товар — эталон номенклатуры. Окно поверх каталога; закрыли — поиск и страница те же.
    kind: PLATFORM_WINDOWS.commonProduct,
    scope: 'global',
    paths: ['/catalog/products/:productId'],
    label: 'Общий товар',
    size: 'md',
    source: { id: 'common', label: 'Общий каталог' },
  },
  {
    kind: PLATFORM_WINDOWS.commonProductNew,
    scope: 'global',
    paths: ['/catalog/product-new'],
    label: 'Новый общий товар',
    size: 'md',
    source: { id: 'common', label: 'Общий каталог' },
  },
  {
    // RP-FB56: режим «выбери товар» общего списка; кадр и назначение — в адресе окна (`frame`, `purpose`), товар не выбран до нажатия.
    kind: PLATFORM_WINDOWS.productPick,
    scope: 'global',
    paths: ['/catalog/pick'],
    label: 'Выбор товара',
    size: 'md',
  },
  {
    // RP-FB56: «Для товара «…» установить это фото?» — выбор «добавить / сделать главным», сохранение, возврат в сканер.
    kind: PLATFORM_WINDOWS.photoAttach,
    scope: 'global',
    paths: ['/catalog/products/:productId/attach-photo'],
    label: 'Прикрепить фото',
    size: 'md',
  },
  {
    kind: PLATFORM_WINDOWS.search,
    scope: 'global',
    paths: ['/search'],
    label: 'Поиск по фото',
    menu: { group: 'work', order: 40, icon: 'photo-search' },
  },
  {
    // RP-FB4: камера, штрихкод с потока, без штрихкода — кадр в поиск по фото; найденное под видоискателем.
    kind: PLATFORM_WINDOWS.scanner,
    scope: 'global',
    paths: ['/search/scan'],
    label: 'Сканер',
    size: 'screen',
  },
  // UI-SECTIONS: разделы меню. Порядок — продажа и чеки, номенклатура, поиск, затем работа с поставками и складом.
  // UI-RMK: «РМК» — работа за кассой любого кассового источника.
  sectionHome(PLATFORM_WINDOWS.rmk, PLATFORM_SECTIONS.rmk, '/rmk', 'РМК', 10, 'sale'),
  // «Чеки» начинаются с выбора источника плитками: чеки 1С и чеки кассы AQSI устроены по-разному, и смотреть их
  // вперемешку нельзя. Внутри выбранного источника — его собственный экран.
  sectionHome(PLATFORM_WINDOWS.receipts, PLATFORM_SECTIONS.receipts, '/receipts', 'Чеки', 20, 'receipts', true),
  // «Статистика по продажам» (решение владельца 26.10.2026): отдельный пункт меню, а не вкладка «Чеков». Чеки ищут и возвращают
  // в «Чеки»; здесь только выручка и итоги, доступ — по праву `sales.read`.
  sectionHome(PLATFORM_WINDOWS.salesStatistics, PLATFORM_SECTIONS.salesStatistics, ['/sales-statistics', '/sales-statistics/sources'], 'Статистика по продажам', 22, 'sales-stats', true),
  // «Смены» (решение владельца 26.10.2026): у каждого источника свой экран смен — как они хранятся в нём (кабинет AQSI, документы 1С).
  // Вкладка источника — по праву чтения его чеков, не по `sales.read`.
  sectionHome(PLATFORM_WINDOWS.shifts, PLATFORM_SECTIONS.shifts, '/shifts', 'Смены', 23, 'shifts', true),
  {
    // Общий каталог — первая вкладка раздела и его содержимое; вкладки ресурсов — их собственные каталоги.
    kind: PLATFORM_WINDOWS.nomenclature,
    scope: 'global',
    paths: ['/nomenclature'],
    label: 'Номенклатура',
    menu: { group: 'work', order: 30, icon: 'catalog' },
    section: { id: PLATFORM_SECTIONS.nomenclature, home: { tab: 'Общий каталог' } },
    // Вкладка помнит, что человек искал и какую страницу смотрел, — не товары.
    capture: (state) => ({ catalog: state.commonCatalog }),
    restore: (snapshot) => {
      const intent = decodeCommonCatalogIntent(snapshot.catalog)
      return intent === undefined ? [] : [catalogIntentRestored(intent)]
    },
  },
  {
    // Собственная вкладка раздела без ресурса: сводка ставки НДС по источникам и каталогу Platform, массовая установка.
    kind: PLATFORM_WINDOWS.nomenclatureSettings,
    scope: 'global',
    paths: ['/nomenclature/settings'],
    label: 'Настройка номенклатуры',
    section: { id: PLATFORM_SECTIONS.nomenclature, ownTab: { label: 'Настройка', order: 1 } },
  },
  sectionHome(PLATFORM_WINDOWS.supplies, PLATFORM_SECTIONS.supplies, '/supplies', 'Поставки', 50, 'supplies', true),
  // «Цены» начинаются с выбора источника плитками (замечание владельца 17.10.2026): цены 1С (записи регистра, виды цен) и цены
  // кабинета AQSI (одна цена карточки) устроены по-разному; внутри источника — его собственное управление ценами.
  sectionHome(PLATFORM_WINDOWS.prices, PLATFORM_SECTIONS.prices, '/prices', 'Цены', 60, 'prices', true),
  sectionHome(PLATFORM_WINDOWS.inventory, PLATFORM_SECTIONS.inventory, '/inventory', 'Инвентаризация', 70, 'inventory', true),
  sectionHome(PLATFORM_WINDOWS.priceTags, PLATFORM_SECTIONS.priceTags, '/price-tags', 'Ценники', 80, 'price-tags', true),
  sectionHome(PLATFORM_WINDOWS.import, PLATFORM_SECTIONS.import, '/import', 'Импорт накладных', 90, 'import', true),
  sectionHome(PLATFORM_WINDOWS.structure, PLATFORM_SECTIONS.structure, '/structure', 'Структура', 100, 'structure', true),
]
